import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from 'recharts'
import { getStaffingMonthly, listScenarios } from '../../api/client'
import { currentLocale } from '../../i18n'
import { useAuth } from '../../context/AuthContext'
import VisibilityMenu from './VisibilityMenu'

/**
 * "Disponibilidad -- grafico": the same committed dotacion as "Por mes", as
 * one stacked bar per month instead of a table. Two independent filters --
 * which projects count, which disciplines count -- apply in EITHER grouping
 * mode: only the mode decides which dimension becomes the bar's own
 * segments (projects in "Por proyecto", disciplines in "Por disciplina");
 * the other dimension's filter just narrows what gets summed into each one.
 *
 * Both filters, and both grouping modes, are built from ONE source:
 * data.projects[project][by_month][key].buckets -- a project's own hours
 * that month, already split by discipline (staffing_matrix.build_staffing_monthly
 * computes this from the same scenario-aware committed-hours source,
 * _planned_hours, so a scenario's effect shows up in every combination of
 * filter and mode with no extra plumbing). data.discipline_totals (the
 * whole-company convenience field "Por mes" itself uses) is NOT used here on
 * purpose -- it cannot be narrowed to a chosen set of projects, only this
 * per-project breakdown can.
 *
 * "Por proyecto" ranks by TOP_N largest among the currently VISIBLE projects
 * (hidden ones don't just lose their segment, they don't count toward
 * anyone's total either), using hours from only the currently visible
 * disciplines -- everything past the top N folds into one "Otros" segment,
 * never a slice per tiny project. "Por disciplina" always shows every
 * visible discipline (including "otros", a real category here, not
 * overflow) -- no folding, since there are only ever five.
 *
 * Colors are a fixed categorical palette assigned by a stable order (rank
 * for projects, the discipline's own fixed order otherwise) -- NOT each
 * project's own Project.color, which defaults to the same #6366f1 for most
 * projects and would paint several "distinct" segments identically.
 * CATEGORICAL is the dataviz skill's own reference palette's first 5 slots
 * (blue/orange/aqua/yellow/magenta) -- that exact order already clears every
 * adjacent-pair contrast/CVD gate for a stacked bar (see
 * references/palette.md), used as-is with no further validation needed.
 * "Otros" is an aggregate, not an identity, so it gets a plain neutral gray
 * outside that set instead.
 */
const WINDOWS = [6, 12, 24]
const TOP_N = 5
// Keep in lockstep with TOP_N/DISCIPLINES -- one slot per series shown at once.
const CATEGORICAL = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4']
const OTHER_COLOR = '#9ca3af'
// Same fixed order and Cargo->bucket classification as StaffingMonthly.jsx's
// own DISCIPLINE_KEY/disciplines.DISCIPLINE_BUCKETS -- never re-derived.
const DISCIPLINES = ['mec', 'elect', 'software', 'factory', 'otros']
const DISCIPLINE_I18N_KEY = {
  mec: 'byMonth.disciplineMec', elect: 'byMonth.disciplineElect',
  software: 'byMonth.disciplineSoftware', factory: 'byMonth.disciplineFactory',
  otros: 'byMonth.disciplineOtros',
}

function monthLabel(year, month) {
  return new Date(year, month - 1, 1).toLocaleDateString(currentLocale(), { month: 'short', year: '2-digit' })
}
function fmtHours(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

export default function StaffingChart() {
  const { t } = useTranslation('clevel')
  const { user } = useAuth()
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [count, setCount] = useState(WINDOWS[1])
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [groupBy, setGroupBy] = useState('project')   // 'project' | 'discipline'
  const [hiddenProjects, setHiddenProjects] = useState(() => new Set())
  const [hiddenDisciplines, setHiddenDisciplines] = useState(() => new Set())
  const [scenarioId, setScenarioId] = useState('')
  const [scenarios, setScenarios] = useState([])

  const k = (suffix, opts) => t(`capacity.chart.${suffix}`, opts)

  const roles = user?.roles || (user?.role ? [user.role] : [])
  // c_level EXACTLY -- mirrors require_exactly("c_level") on scenario_id
  // (routers/capacity.py), same as the other three Disponibilidad views.
  const canScenarios = roles.includes('c_level')

  useEffect(() => {
    if (!canScenarios) return
    listScenarios().then(setScenarios).catch(() => {})
  }, [canScenarios])

  useEffect(() => {
    getStaffingMonthly({ year, month, count, scenarioId: canScenarios ? scenarioId : '' })
      .then(d => { setData(d); setError('') })
      .catch(err => setError(err.response?.data?.detail || k('loadFailed')))
  }, [year, month, count, scenarioId, canScenarios]) // eslint-disable-line react-hooks/exhaustive-deps

  const shift = (delta) => {
    let y = year
    let m = month + delta * count
    while (m > 12) { m -= 12; y += 1 }
    while (m < 1) { m += 12; y -= 1 }
    setYear(y)
    setMonth(m)
  }

  const toggleHiddenProject = (id) => setHiddenProjects(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleHiddenDiscipline = (d) => setHiddenDisciplines(prev => {
    const next = new Set(prev)
    next.has(d) ? next.delete(d) : next.add(d)
    return next
  })

  if (error) return <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>
  if (!data) return <p className="text-sm text-gray-400 px-1">{t('capacity.loading')}</p>

  // Already sorted by total hours descending, server-side -- irrelevant here
  // since visibleProjects gets its own ranking below, from filtered hours.
  const allProjects = data.projects
  const visibleProjects = allProjects.filter(p => !hiddenProjects.has(p.project_id))
  const visibleDisciplines = DISCIPLINES.filter(d => !hiddenDisciplines.has(d))

  // A project's hours in one month, counting only the currently visible
  // disciplines -- the one building block both modes and both filters share.
  const filteredMonthHours = (project, monthKey) => {
    const buckets = project.by_month[monthKey]?.buckets || {}
    let sum = 0
    for (const d of visibleDisciplines) sum += buckets[d] || 0
    return sum
  }
  const filteredTotal = (project) =>
    data.months.reduce((sum, m) => sum + filteredMonthHours(project, m.key), 0)

  const rankedProjects = [...visibleProjects]
    .map(p => ({ ...p, _filteredTotal: filteredTotal(p) }))
    .sort((a, b) => b._filteredTotal - a._filteredTotal)
  const topProjects = rankedProjects.slice(0, TOP_N)
  const otherProjects = rankedProjects.slice(TOP_N)
  const hasOther = otherProjects.length > 0

  // One shape either way: {key, name, color}[] naming the bars to draw --
  // chartData below fills in each one's values, but the chart markup itself
  // never branches on mode.
  const series = groupBy === 'discipline'
    ? visibleDisciplines.map((d) => ({
        key: d, name: t(`capacity.${DISCIPLINE_I18N_KEY[d]}`), color: CATEGORICAL[DISCIPLINES.indexOf(d)],
      }))
    : topProjects.map((p, i) => ({ key: `p${p.project_id}`, name: p.name, color: CATEGORICAL[i] }))
  const otherSeries = groupBy === 'project' && hasOther
    ? { key: 'other', name: k('otherProjects'), color: OTHER_COLOR }
    : null

  const chartData = data.months.map(m => {
    const row = { label: monthLabel(m.year, m.month) }
    if (groupBy === 'discipline') {
      for (const d of visibleDisciplines) {
        row[d] = visibleProjects.reduce((sum, p) => sum + (p.by_month[m.key]?.buckets?.[d] || 0), 0)
      }
    } else {
      for (const p of topProjects) {
        row[`p${p.project_id}`] = filteredMonthHours(p, m.key)
      }
      if (hasOther) {
        row.other = otherProjects.reduce((sum, p) => sum + filteredMonthHours(p, m.key), 0)
      }
    }
    return row
  })

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          {WINDOWS.map(size => (
            <button key={size} onClick={() => setCount(size)}
              className={`px-3 py-1.5 ${count === size ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k('window', { count: size })}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button onClick={() => shift(-1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">‹</button>
          <span className="text-sm font-semibold text-gray-900 px-2 min-w-28 text-center">
            {monthLabel(year, month)}
          </span>
          <button onClick={() => shift(1)}
            className="px-2 py-1 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">›</button>
        </div>

        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          {['project', 'discipline'].map(g => (
            <button key={g} onClick={() => setGroupBy(g)}
              className={`px-3 py-1.5 ${groupBy === g ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {k(`groupBy_${g}`)}
            </button>
          ))}
        </div>

        <VisibilityMenu
          label={k('filterProjects')}
          items={allProjects}
          hidden={hiddenProjects}
          getKey={p => p.project_id}
          getLabel={p => p.name}
          getHint={p => fmtHours(p.total)}
          onToggle={toggleHiddenProject}
          onShowAll={() => setHiddenProjects(new Set())}
          onHideAll={() => setHiddenProjects(new Set(allProjects.map(p => p.project_id)))}
          allLabel={k('showAll')}
          noneLabel={k('showNone')}
          emptyLabel={k('noStaffing')}
        />

        <VisibilityMenu
          label={k('filterDisciplines')}
          items={DISCIPLINES}
          hidden={hiddenDisciplines}
          getKey={d => d}
          getLabel={d => t(`capacity.${DISCIPLINE_I18N_KEY[d]}`)}
          onToggle={toggleHiddenDiscipline}
          onShowAll={() => setHiddenDisciplines(new Set())}
          onHideAll={() => setHiddenDisciplines(new Set(DISCIPLINES))}
          allLabel={k('showAll')}
          noneLabel={k('showNone')}
          emptyLabel={k('noStaffing')}
        />

        {canScenarios && (
          <select
            value={scenarioId}
            onChange={e => setScenarioId(e.target.value)}
            className={`text-xs border rounded-lg px-2 py-1.5 bg-white ${
              scenarioId ? 'border-amber-300 text-amber-800' : 'border-gray-200 text-gray-700'}`}
          >
            <option value="">{k('realPlan')}</option>
            {scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>

      {allProjects.length === 0 ? (
        <p className="text-sm text-gray-400 px-1">{k('noStaffing')}</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <ResponsiveContainer width="100%" height={420}>
            <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} width={48}
                     label={{ value: k('yAxis'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6b7280' } }} />
              <Tooltip formatter={(v, name) => [`${fmtHours(v)} h`, name]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {series.map(s => (
                <Bar key={s.key} dataKey={s.key} stackId="a" fill={s.color} name={s.name} />
              ))}
              {otherSeries && (
                <Bar key={otherSeries.key} dataKey={otherSeries.key} stackId="a"
                     fill={otherSeries.color} name={otherSeries.name} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
